import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import {
    UserFeatureProgressStore,
    UserFeatureProgressStoreType,
} from '../../../../core/store/user-feature-progress.store';
import { OnboardingSteps, UserFeatureType } from '../../../../shared/types/user-feature-progress';
import { UserFeatureProgressService } from '../../../../core/services/user-feature-progress.service';
import { LayoutStore, LayoutStoreType } from '../../../../core/store/layout.store';
import { InfoPopupModel } from '../../../../shared/components/popup/info-popup/info-popup.model';
import { uuidv4 } from '../../../../shared/utils/unique-id-utils';

@Component({
    selector: 'juyo-getting-started',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: 'getting-started.scss',
    template: `
        @if (visible()) {
            <div class="getting-started">
                <div class="header" (click)="onToggleWidgetCollapsed()">
                    <span class="material-symbols-outlined icon">history_edu</span>
                    <span class="title">Getting Started</span>
                    <span class="count">{{ completedCount() }}/{{ stepCount }}</span>
                    <span class="material-symbols-outlined chevron">
                        {{ widgetCollapsed() ? 'expand_less' : 'expand_more' }}
                    </span>
                    <span class="material-symbols-outlined close"
                          (click)="onDismiss(); $event.stopPropagation()">
                        close
                    </span>
                </div>
                <div class="progress-bar">
                    <div class="progress-bar__fill" [style.width.%]="progressPercent()"></div>
                </div>
                @if (!widgetCollapsed()) {
                    <ul class="steps">
                        @for (step of steps(); track step.key) {
                            <li [class.done]="step.done" [class.current]="step.isCurrent">
                                <span class="step-indicator">
                                    @if (step.done) {
                                        <span class="material-symbols-outlined check">check</span>
                                    }
                                </span>
                                <span class="step-label">{{ step.label }}</span>
                            </li>
                        }
                    </ul>
                }
            </div>
        }
    `,
})
export class GettingStartedComponent {
    private store: UserFeatureProgressStoreType = inject(UserFeatureProgressStore);
    private service = inject(UserFeatureProgressService);
    private layoutStore: LayoutStoreType = inject(LayoutStore);

    public collapsed = input.required<boolean>();

    protected widgetCollapsed = signal(false);
    protected readonly stepCount = STEP_ORDER.length;

    private everVisible = false;
    private wasCompleted = false;
    private celebrationShown = false;

    constructor() {
        effect(() => {
            const visible = this.visible();
            const completed = !!this.progress()?.completedAt;

            // Track whether the widget was actually shown to this user this session.
            if (visible) this.everVisible = true;

            // Celebrate once when onboarding completes (completedAt becomes set), but only
            // if the widget had been visible — so we don't fire for already-onboarded users
            // or when the widget was merely collapsed/dismissed.
            const justCompleted = completed && !this.wasCompleted;
            if (justCompleted && this.everVisible && !this.celebrationShown) {
                this.celebrationShown = true;
                this.layoutStore.showPopup(
                    new InfoPopupModel(uuidv4(), 'Nice work! Your initial setup is complete. 🎉', 'Continue exploring'),
                );
            }

            this.wasCompleted = completed;
        });
    }

    protected progress = computed(() => {
        const progressMap = this.store.progressMap();

        return progressMap[UserFeatureType.Onboarding];
    });

    protected visible = computed(() => {
        const progress = this.progress();
        const collapsed = this.collapsed();
        const completedCount = this.completedCount();

        return !collapsed && !!progress && !progress.completedAt && completedCount < this.stepCount;
    });

    protected completedCount = computed(() => {
        const stepMap = this.progress()?.steps ?? {};
        return STEP_ORDER.filter((key) => !!stepMap[key]).length;
    });

    protected progressPercent = computed(() => (this.completedCount() / STEP_ORDER.length) * 100);

    protected steps = computed<StepItem[]>(() => {
        const stepMap = this.progress()?.steps ?? {};
        let currentFound = false;
        return STEP_ORDER.map((key) => {
            const done = !!stepMap[key];
            const isCurrent = !done && !currentFound;
            if (isCurrent) currentFound = true;
            return { key, label: STEP_LABELS[key], done, isCurrent };
        });
    });

    protected onToggleWidgetCollapsed = (): void => {
        this.widgetCollapsed.update((c) => !c);
    };

    protected onDismiss(): void {
        this.service
            .dismissUserFeatureProgress(UserFeatureType.Onboarding)
            .subscribe((progress) => this.store.setProgress(UserFeatureType.Onboarding, progress));
    }
}

interface StepItem {
    key: OnboardingSteps;
    label: string;
    done: boolean;
    isCurrent: boolean;
}

const STEP_ORDER: OnboardingSteps[] = [
    OnboardingSteps.SetupProperty,
    OnboardingSteps.ConnectPms,
    OnboardingSteps.DataDrop,
    OnboardingSteps.Invite,
    OnboardingSteps.Question,
];

const STEP_LABELS: Record<OnboardingSteps, string> = {
    [OnboardingSteps.SetupProperty]: 'Set up your property',
    [OnboardingSteps.ConnectPms]: 'Connect your PMS',
    [OnboardingSteps.DataDrop]: 'Try Data Drop',
    [OnboardingSteps.Invite]: 'Invite a teammate',
    [OnboardingSteps.Question]: 'Ask your first question',
};
